import { colors } from '@apollo/brand';
import { useMemo } from 'react';

import { HeatmapStackConfig } from 'src/components/charts/Heatmap';
import { HistogramBucket } from 'src/components/charts/helpers/histogram';
import { useStackInfo } from 'src/components/charts/helpers/useStackInfo';
import { zeroPadTimeSeriesData } from 'src/components/charts/helpers/zeroPadTimeSeriesData';
import { decodeSparseBuckets } from 'src/lib/decodeSparseBuckets';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import { FetchLatencyOverTimeResultFragment } from 'src/lib/graphqlTypes/types';

export interface FetchHeatmapData {
  timestamp: Timestamp['output'];
  value: {
    sparseBuckets: number[];
    buckets: HistogramBucket[];
  };
}

export type FetchHeatmapMetricType = 'heatmap';

export const useFetchHeatmapStackInfo = (
  resolution: GraphQLTypes.Resolution,
  result?: FetchLatencyOverTimeResultFragment,
) => {
  const normalizedData: FetchHeatmapData[] = useMemo(
    () =>
      result
        ? zeroPadTimeSeriesData({
            data: result.records,
            timeFrom: new Date(result.roundedDownFrom),
            timeTo: new Date(result.roundedUpTo),
            resolution,
            getValue: (record) =>
              record.data?.fetchLatencyHistogram?.sparseBuckets
                ? {
                    sparseBuckets:
                      record.data.fetchLatencyHistogram.sparseBuckets,
                    buckets: decodeSparseBuckets(
                      record.data.fetchLatencyHistogram.sparseBuckets,
                    ),
                  }
                : {
                    sparseBuckets: [],
                    buckets: [],
                  },
            getZeroFillValue: () => ({
              sparseBuckets: [],
              buckets: [],
            }),
          })
        : [],
    [resolution, result],
  );

  const stacks = useMemo(
    (): MinLengthArray<1, HeatmapStackConfig<FetchHeatmapData>> => [
      {
        primaryColor: colors.primitives.blue[300],
        data: normalizedData,
        getValue: ({ timestamp, value }: FetchHeatmapData) => ({
          timestamp,
          sparseBuckets: value.sparseBuckets,
          buckets: value.buckets,
        }),
        hasData: ({ value }: FetchHeatmapData) =>
          value.sparseBuckets.some((v) => v > 0),
        label: 'Fetches',
        unit: ' RPM',
      },
    ],
    [normalizedData],
  );
  return useStackInfo<
    FetchHeatmapData,
    { timestamp: Timestamp['output']; buckets: HistogramBucket[] },
    HeatmapStackConfig<FetchHeatmapData>
  >(stacks);
};
